import { useEffect, useMemo, useRef, useState } from "react";

/**
 * RevealAvatar — cinematic identity reveal for BlinkLink matches.
 *
 * Behavior (spec):
 *   • Start with a heavily-blurred glass version of the avatar behind a subtle
 *     dark tint, then smoothly dissolve to the clear photo.
 *   • Concurrently reveal the person's name with a typewriter effect.
 *   • Play the animation exactly ONCE per (src,name) pair — subsequent
 *     re-renders show the clear state so we never re-animate on WS updates.
 *   • Graceful fallback: if `src` is null we skip the photo animation entirely
 *     and only run the name typewriter (renders the given `placeholder` node
 *     for the visual anchor). Layout size is fixed by `size` so there's no
 *     jitter between placeholder and image.
 *
 * PRIVACY: This component draws whatever pixels it is handed. The decision of
 * *whether* to reveal is made server-side by `public_profile()` (Ghost = null,
 * Visible or opt-in reveal = the URL). We do nothing to bypass that.
 *
 * Props:
 *   src            image URL, or falsy for "no avatar" fallback
 *   name           string to typewriter-reveal (real_name or display_name)
 *   size           px, both width & height (default 112)
 *   showName       whether to render the typewriter name line (default true)
 *   placeholder    ReactNode rendered in place of <img> when src is falsy
 *   testid         base data-testid ("reveal-avatar" → adds -img, -name)
 */
export default function RevealAvatar({
  src,
  name,
  size = 112,
  showName = true,
  placeholder = null,
  testid = "reveal-avatar",
}) {
  // Cache of already-animated identities so re-mounts (e.g. tab switches or
  // React 18 strict-mode double-mount) don't retrigger the effect.
  const seenKey = `${src || ""}|${name || ""}`;
  const [phase, setPhase] = useState(() =>
    _seenReveals.has(seenKey) ? "clear" : "blurred"
  );
  const typedRef = useRef("");
  const [typed, setTyped] = useState(_seenReveals.has(seenKey) ? (name || "") : "");

  useEffect(() => {
    if (_seenReveals.has(seenKey)) return undefined;
    // Photo dissolve: 400ms hold on blurred, then 900ms transition to clear.
    const t1 = setTimeout(() => setPhase("clear"), 400);
    // Name typewriter — starts a bit after the photo begins to sharpen.
    let cancelled = false;
    const startType = 700;
    const perChar = 55; // ms — ~18 chars/sec, feels crisp
    const chars = (name || "").split("");
    const runTypewriter = () => {
      if (cancelled) return;
      typedRef.current = "";
      setTyped("");
      chars.forEach((ch, i) => {
        setTimeout(() => {
          if (cancelled) return;
          typedRef.current += ch;
          setTyped(typedRef.current);
        }, i * perChar);
      });
    };
    const t2 = setTimeout(runTypewriter, startType);
    const done = setTimeout(() => {
      _seenReveals.add(seenKey);
    }, startType + chars.length * perChar + 50);
    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(done);
    };
  }, [seenKey, name]);

  const boxStyle = useMemo(() => ({ width: size, height: size }), [size]);
  const isClear = phase === "clear";

  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div
        className="relative rounded-full overflow-hidden border-2 border-white/20 shadow-[0_0_36px_rgba(230,0,0,0.35)]"
        style={boxStyle}
      >
        {src ? (
          <>
            <img
              src={src}
              alt=""
              className="w-full h-full object-cover transition-all duration-[900ms] ease-out"
              style={{
                filter: isClear ? "blur(0px)" : "blur(24px) saturate(0.7) brightness(0.85)",
                transform: isClear ? "scale(1)" : "scale(1.08)",
              }}
              data-testid={`${testid}-img`}
              data-phase={phase}
            />
            {/* Glass tint that fades away as the photo sharpens */}
            <div
              className="absolute inset-0 backdrop-blur-md transition-opacity duration-[900ms] ease-out"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(0,0,0,0.35) 0%, rgba(230,0,0,0.15) 60%, rgba(0,0,0,0.5) 100%)",
                opacity: isClear ? 0 : 1,
              }}
              aria-hidden="true"
            />
            {/* Highlight sweep during reveal for a subtle "unlocked" flash */}
            <div
              className={`pointer-events-none absolute inset-0 ${
                isClear ? "opacity-0" : "opacity-100 reveal-sweep"
              }`}
              style={{
                background:
                  "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)",
                transition: "opacity 900ms ease-out",
              }}
              aria-hidden="true"
            />
          </>
        ) : (
          placeholder || (
            <div className="w-full h-full bg-gradient-to-br from-black to-[#1a0000] flex items-center justify-center text-zinc-500 text-xs font-mono-accent">
              GHOST
            </div>
          )
        )}
      </div>
      {showName && name && (
        <p
          className="mt-4 text-white text-2xl font-bold tracking-tight text-center"
          data-testid={`${testid}-name`}
          aria-label={name}
        >
          <span>{typed}</span>
          <span
            className={`inline-block w-[2px] h-6 align-middle bg-white ml-1 ${
              typed.length < (name || "").length ? "animate-pulse" : "opacity-0"
            }`}
            aria-hidden="true"
          />
        </p>
      )}
      <style>{`
        @keyframes reveal-sweep {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .reveal-sweep {
          animation: reveal-sweep 1100ms ease-out 200ms 1 both;
        }
      `}</style>
    </div>
  );
}

// Module-scoped set — same identity revealed twice in the same tab session
// (e.g. user backs out and re-opens the match) shouldn't re-trigger the intro
// animation. Cleared on hard refresh, which is desirable.
const _seenReveals = new Set();
